export type ModularBundleDiscountProduct = {
  name: string;
  // Required (nullable): callers must pass the entitlement profile key when they
  // have one so a product is never silently classified by name. Products with no
  // key (Blue/Pause/Agency, legacy Stripe prices) pass null explicitly, which
  // routes to the name fallback in matchesProductClassification.
  entitlementProfileKey: string | null;
};

const LEGACY_MODULAR_BUNDLE_COUPON_ID_PREFIX = "modular_2026_bundle_";

// Stripe coupon IDs are opaque in live/test Stripe, while the original planned
// IDs used the readable modular_2026_bundle_ prefix. Keep accepting the legacy
// prefix for historical/admin data, but use these concrete Stripe coupon IDs for
// current bundle discount application and display labels. If one stops working,
// search these IDs in the Stripe Coupons dashboard before changing source.
export const MODULAR_BUNDLE_COUPON_IDS = {
  fivePercent: "3NYbG13J",
  tenPercent: "qL5wCf1g",
  fifteenPercent: "hvIPzePE",
} as const;

const MODULAR_BUNDLE_COUPON_ID_SET = new Set<string>(
  Object.values(MODULAR_BUNDLE_COUPON_IDS)
);

const MODULAR_BUNDLE_COUPON_DISPLAY_LABELS = {
  [MODULAR_BUNDLE_COUPON_IDS.fivePercent]: "5% modular bundle discount coupon",
  [MODULAR_BUNDLE_COUPON_IDS.tenPercent]: "10% modular bundle discount coupon",
  [MODULAR_BUNDLE_COUPON_IDS.fifteenPercent]:
    "15% modular bundle discount coupon",
} as const;

const STARTER_ENTITLEMENT_PROFILE_KEYS = ["starter"] as const;
const UNLIMITED_ENTITLEMENT_PROFILE_KEYS = ["unlimited"] as const;
const SMART_CONTENT_ENTITLEMENT_PROFILE_KEYS = ["smart-content"] as const;
const SMART_PRICING_ENTITLEMENT_PROFILE_KEYS = [
  "smart-pricing-limited",
  "smart-pricing-unlimited",
] as const;
// Add-ons are billable line items that grant no entitlements: they sit outside
// the bundle discount ladder and never trigger the Smart Content requirement.
const ADD_ON_ENTITLEMENT_PROFILE_KEYS = ["agentic-optimization"] as const;

function matchesEntitlementProfileKey(
  product: ModularBundleDiscountProduct,
  entitlementProfileKeys: readonly string[]
) {
  return (
    product.entitlementProfileKey != null &&
    entitlementProfileKeys.includes(product.entitlementProfileKey)
  );
}

function matchesProductClassification(
  product: ModularBundleDiscountProduct,
  entitlementProfileKeys: readonly string[],
  nameIncludes: string
) {
  if (product.entitlementProfileKey != null) {
    return matchesEntitlementProfileKey(product, entitlementProfileKeys);
  }

  return product.name.toLowerCase().includes(nameIncludes);
}

export function isModularBundleCouponId(couponId?: string | null) {
  return (
    typeof couponId === "string" &&
    (couponId.startsWith(LEGACY_MODULAR_BUNDLE_COUPON_ID_PREFIX) ||
      MODULAR_BUNDLE_COUPON_ID_SET.has(couponId))
  );
}

export function getModularBundleCouponDisplayLabel(couponId: string) {
  return (
    MODULAR_BUNDLE_COUPON_DISPLAY_LABELS[
      couponId as keyof typeof MODULAR_BUNDLE_COUPON_DISPLAY_LABELS
    ] ?? couponId
  );
}

export function isStarterProduct(product: ModularBundleDiscountProduct) {
  return matchesProductClassification(
    product,
    STARTER_ENTITLEMENT_PROFILE_KEYS,
    "starter"
  );
}

export function isBlueProduct(product: ModularBundleDiscountProduct) {
  return product.name.toLowerCase().includes("blue");
}

export function isUnlimitedProduct(product: ModularBundleDiscountProduct) {
  return matchesProductClassification(
    product,
    UNLIMITED_ENTITLEMENT_PROFILE_KEYS,
    "unlimited"
  );
}

export function isSmartPricingProduct(product: ModularBundleDiscountProduct) {
  return matchesProductClassification(
    product,
    SMART_PRICING_ENTITLEMENT_PROFILE_KEYS,
    "smart pricing"
  );
}

export function isSmartContentProduct(product: ModularBundleDiscountProduct) {
  return matchesProductClassification(
    product,
    SMART_CONTENT_ENTITLEMENT_PROFILE_KEYS,
    "smart content"
  );
}

export function isPauseProduct(
  product: Pick<ModularBundleDiscountProduct, "name">
) {
  return product.name.toLowerCase().includes("pause");
}

export function isAgencyProduct(product: ModularBundleDiscountProduct) {
  return product.name.toLowerCase().includes("agency");
}

export function isAddOnProduct(product: ModularBundleDiscountProduct) {
  return matchesProductClassification(
    product,
    ADD_ON_ENTITLEMENT_PROFILE_KEYS,
    "agentic optimization"
  );
}

export function isBasePlatformProduct(
  product: Pick<ModularBundleDiscountProduct, "name">
) {
  return product.name.toLowerCase().includes("platform");
}

// The base platform fee and add-ons are billable but seed no scopes, so a
// target built only from them would bill the org for nothing.
export function isEntitlementGrantingProduct(
  product: ModularBundleDiscountProduct
) {
  return !isAddOnProduct(product) && !isBasePlatformProduct(product);
}

// An add-on is a rider: it must attach to a product that actually grants
// something. Scoped to targets containing an add-on so platform-only targets
// keep their existing treatment.
export function isAddOnOnlyTarget(products: ModularBundleDiscountProduct[]) {
  return (
    products.some(isAddOnProduct) &&
    !products.some(isEntitlementGrantingProduct)
  );
}

export function isBundleEligibleProduct(product: ModularBundleDiscountProduct) {
  return (
    !isStarterProduct(product) &&
    !isBlueProduct(product) &&
    !isUnlimitedProduct(product) &&
    !isPauseProduct(product) &&
    !isAddOnProduct(product)
  );
}

// Unlimited is the "everything bundle" and so already satisfies the Smart
// Content floor for the whole target — including any module sitting alongside
// it, which is why the check is over the set rather than per product.
export function targetRequiresSmartContent(
  products: ModularBundleDiscountProduct[]
) {
  return (
    products.some(isBundleEligibleProduct) && !products.some(isUnlimitedProduct)
  );
}

const MODULAR_BUNDLE_COUPON_ID_BY_PERCENTAGE: Record<number, string> = {
  0.05: MODULAR_BUNDLE_COUPON_IDS.fivePercent,
  0.1: MODULAR_BUNDLE_COUPON_IDS.tenPercent,
  0.15: MODULAR_BUNDLE_COUPON_IDS.fifteenPercent,
};

// The percent a bundle coupon takes off, in Stripe's `percent_off` units
// (10 = 10%), so a caller holding an expected coupon id can compare it against
// a live discount without a second source for the ladder. Null for ids outside
// the current ladder, including legacy ids, whose percent is not encoded here.
export function getModularBundleCouponPercentOff(couponId?: string | null) {
  const entry = Object.entries(MODULAR_BUNDLE_COUPON_ID_BY_PERCENTAGE).find(
    ([, id]) => id === couponId
  );
  return entry ? Math.round(Number(entry[0]) * 100) : null;
}

// Single source of truth for the modular bundle discount ladder: 0/5/10/15%
// for 1/2/3/4+ eligible modules. Shared by resolveModularBundleDiscount (real,
// product-based eligibility) and consumers previewing a hypothetical count
// (e.g. "add 1 more module" upsell copy) so both stay on the same thresholds.
export function getModularBundleDiscountPercentageForModuleCount(
  eligibleModuleCount: number
): number {
  if (eligibleModuleCount >= 4) {
    return 0.15;
  }

  if (eligibleModuleCount === 3) {
    return 0.1;
  }

  if (eligibleModuleCount === 2) {
    return 0.05;
  }

  return 0;
}

export function resolveModularBundleDiscount(
  products: ModularBundleDiscountProduct[]
): {
  eligibleModuleCount: number;
  percentage: number;
  couponId: string | null;
} {
  const hasSmartContent = products.some(isSmartContentProduct);
  const eligibleModuleCount = hasSmartContent
    ? products.filter(isBundleEligibleProduct).length
    : 0;
  const percentage =
    getModularBundleDiscountPercentageForModuleCount(eligibleModuleCount);

  return {
    eligibleModuleCount,
    percentage,
    couponId: MODULAR_BUNDLE_COUPON_ID_BY_PERCENTAGE[percentage] ?? null,
  };
}
